import { DeleteIcon, RepeatIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useApi from "../../../shared/hooks/useApi";
import useSocketEvent from "../../../shared/hooks/useSocketEvent";
import { components, PatientId, PatientServicePlanId } from "../../../shared/schema/schema";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import ServicePlanEditorModal from "./ServicePlanEditorModal";
import ServicePlanStatusTag from "./ServicePlanStatusTag";

type ServicePlanListItem = components["schemas"]["PatientServicePlanListItem"];
type ServicePlanImportListItem = components["schemas"]["PatientServicePlanImportListItem"];

const ACCEPTED_FILE_TYPES = ".pdf,.xlsx,.csv,.png,.jpg,.jpeg";

interface Props {
  patientId: PatientId;
}

export default function ServicePlanList({ patientId }: Props) {
  const queryClient = useQueryClient();
  const { queries, mutations } = useApi();
  const toast = useToast();
  const [deletingPlan, setDeletingPlan] = useState<ServicePlanListItem | null>(null);
  const [editingPlanId, setEditingPlanId] = useState<PatientServicePlanId | null>(null);

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  const servicePlans = useQuery(queries.servicePlan.list(patientId));

  const invalidateList = () => {
    queryClient.invalidateQueries({
      queryKey: queries.servicePlan.list(patientId).queryKey,
    });
  };

  useSocketEvent({
    key: "PatientServicePlanImportCompleted",
    onEvent: (event) => {
      if (event.patientId !== patientId) {
        return;
      }
      invalidateList();
      toast({
        title: event.success ? "Calculator import finished" : "Calculator import failed",
        status: event.success ? "success" : "error",
        position: "top-right",
        isClosable: true,
      });
    },
  });

  const uploadMutation = useMutation({
    ...mutations.servicePlan.uploadCalculator(patientId),
    onSuccess: () => {
      invalidateList();
      toast({
        title: "Calculator uploaded",
        description: "Extraction started — this usually takes about a minute.",
        status: "success",
        position: "top-right",
        isClosable: true,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Upload failed",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const retryMutation = useMutation({
    ...mutations.servicePlan.retryImport(),
    onSuccess: invalidateList,
  });

  const pdfUrlMutation = useMutation({
    ...mutations.servicePlan.getPdfUrl(),
    onSuccess: ({ signedUrl }) => {
      if (signedUrl === null) {
        toast({
          title: "No PDF for this plan",
          status: "warning",
          position: "top-right",
        });
        return;
      }
      window.open(signedUrl, "_blank");
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not open the PDF",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const deleteMutation = useMutation({
    ...mutations.servicePlan.deleteServicePlan(),
    onSuccess: () => {
      invalidateList();
      handleDeleteModalClose();
    },
  });

  const handleUploadClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ACCEPTED_FILE_TYPES;
    input.style.display = "none";
    input.onchange = () => {
      const file = input.files?.[0];
      if (file !== undefined) {
        uploadMutation.mutate(file);
      }
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  };

  const handleDelete = (plan: ServicePlanListItem) => {
    setDeletingPlan(plan);
    onDeleteModalOpen();
  };

  const handleDeleteModalClose = () => {
    setDeletingPlan(null);
    onDeleteModalClose();
  };

  const confirmDelete = () => {
    if (deletingPlan !== null) {
      deleteMutation.mutate(deletingPlan.id);
    }
  };

  switch (servicePlans.status) {
    case "error":
      return (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading service plans</AlertTitle>
          <AlertDescription>Please try again later.</AlertDescription>
        </Alert>
      );

    case "pending":
      return <Text>Loading service plans...</Text>;

    case "success": {
      const activeImports = servicePlans.data.imports.filter((item) => item.status !== "completed");

      return (
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between">
            <Text fontSize="lg" fontWeight="semibold">
              CDSC Service Plans
            </Text>
            <Button
              colorScheme="blue"
              isLoading={uploadMutation.isPending}
              loadingText="Uploading..."
              onClick={handleUploadClick}
            >
              Upload calculator
            </Button>
          </HStack>

          {activeImports.length > 0 && (
            <VStack align="stretch" spacing={2}>
              {activeImports.map((item) => (
                <ImportRow
                  key={item.id}
                  isRetrying={retryMutation.isPending}
                  item={item}
                  onRetry={() => retryMutation.mutate(item.id)}
                />
              ))}
            </VStack>
          )}

          {servicePlans.data.servicePlans.length === 0 && activeImports.length === 0 ? (
            <Box color="gray.500" py={8} textAlign="center">
              <Text fontSize="lg">No service plans yet.</Text>
              <Text fontSize="md">
                Upload a CFC Direct Care Services Calculator file to get started.
              </Text>
            </Box>
          ) : (
            servicePlans.data.servicePlans.length > 0 && (
              <TableContainer>
                <Table size="md">
                  <Thead>
                    <Tr>
                      <Th>Status</Th>
                      <Th>Period</Th>
                      <Th>Program</Th>
                      <Th>Service</Th>
                      <Th>Case manager</Th>
                      <Th>Tasks</Th>
                      <Th>Created</Th>
                      <Th width="80px">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {servicePlans.data.servicePlans.map((plan) => (
                      <Tr key={plan.id}>
                        <Td>
                          <ServicePlanStatusTag status={plan.status} />
                        </Td>
                        <Td>
                          {plan.startDate === null ? "—" : dateFormatter.toDate(plan.startDate)}
                          {" – "}
                          {plan.endDate === null ? "—" : dateFormatter.toDate(plan.endDate)}
                        </Td>
                        <Td>{plan.program ?? "—"}</Td>
                        <Td>{plan.service ?? "—"}</Td>
                        <Td>{plan.caseManagerName ?? "—"}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Text>{plan.tasksCount}</Text>
                            {plan.unmappedTasksCount > 0 && (
                              <Badge colorScheme="orange">{plan.unmappedTasksCount} unmapped</Badge>
                            )}
                          </HStack>
                        </Td>
                        <Td>{dateFormatter.toDate(plan.createdAt)}</Td>
                        <Td>
                          <HStack spacing={1}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingPlanId(plan.id)}
                            >
                              {plan.status === "DRAFT" ? "Edit" : "View"}
                            </Button>
                            {plan.hasPdf && (
                              <Button
                                isLoading={
                                  pdfUrlMutation.isPending && pdfUrlMutation.variables === plan.id
                                }
                                size="sm"
                                variant="outline"
                                onClick={() => pdfUrlMutation.mutate(plan.id)}
                              >
                                PDF
                              </Button>
                            )}
                            {plan.status === "DRAFT" && (
                              <IconButton
                                aria-label="Delete service plan"
                                colorScheme="red"
                                icon={<DeleteIcon />}
                                size="md"
                                variant="ghost"
                                onClick={() => handleDelete(plan)}
                              />
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )
          )}

          {editingPlanId !== null && (
            <ServicePlanEditorModal
              isOpen={true}
              patientId={patientId}
              servicePlanId={editingPlanId}
              onClose={() => setEditingPlanId(null)}
            />
          )}

          <Modal isOpen={isDeleteModalOpen} onClose={handleDeleteModalClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Delete Service Plan</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text>
                  Are you sure you want to delete this draft service plan? This action cannot be
                  undone.
                </Text>
              </ModalBody>
              <ModalFooter>
                <Button mr={3} variant="ghost" onClick={handleDeleteModalClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isLoading={deleteMutation.isPending}
                  onClick={confirmDelete}
                >
                  Delete
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </VStack>
      );
    }
  }
}

function ImportRow(props: {
  isRetrying: boolean;
  item: ServicePlanImportListItem;
  onRetry: () => void;
}) {
  const { isRetrying, item, onRetry } = props;

  return (
    <HStack borderRadius="md" borderWidth="1px" justify="space-between" paddingX={4} paddingY={2}>
      <HStack spacing={3}>
        <ImportStatusIndicator status={item.status} />
        <VStack align="start" spacing={0}>
          <Text fontWeight="medium">{item.fileName}</Text>
          <Text color="gray.500" fontSize="sm">
            Uploaded {dateFormatter.toDateTime(item.createdAt)}
          </Text>
          {item.status === "failed" && item.error !== null && (
            <Text color="red.500" fontSize="sm">
              {item.error}
            </Text>
          )}
        </VStack>
      </HStack>
      {item.status === "failed" && (
        <IconButton
          aria-label="Retry import"
          icon={<RepeatIcon />}
          isLoading={isRetrying}
          size="md"
          variant="ghost"
          onClick={onRetry}
        />
      )}
    </HStack>
  );
}

function ImportStatusIndicator(props: { status: ServicePlanImportListItem["status"] }) {
  switch (props.status) {
    case "pending":
    case "processing":
      return (
        <HStack spacing={2}>
          <Spinner size="sm" />
          <Tag colorScheme="blue">Extracting</Tag>
        </HStack>
      );
    case "failed":
      return <Tag colorScheme="red">Failed</Tag>;
    case "completed":
      return <Tag colorScheme="green">Completed</Tag>;
  }
}
